import {
  formatNumber,
  formatPercent,
  formatEstimatedUsd,
  type Language,
  type Translate,
} from '@athena/i18n'
import type { AiDiagnosticsSort, AiAnalyticsFilterInput } from '../graphql/generated/operations.js'
export interface AIAnalyticsSearch {
  from?: string
  to?: string
  provider?: string
  model?: string
  operation?: 'PRACTICE_GENERATION' | 'PRACTICE_EXTRACTION'
  version?: number
  status?: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'ABANDONED'
  assignment?: 'EXPERIMENT' | 'OVERRIDE' | 'DEFAULT' | 'EXTRACTION'
  page?: number
  sort?: AiDiagnosticsSort
}
const day = /^\d{4}-\d{2}-\d{2}$/
export function validDay(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    day.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  )
}
export function parseAIAnalyticsSearch(raw: Record<string, unknown>): AIAnalyticsSearch {
  const value: AIAnalyticsSearch = {}
  for (const key of ['from', 'to'] as const) if (validDay(raw[key])) value[key] = raw[key]
  if (raw['provider'] === 'openai' || raw['provider'] === 'gemini') value.provider = raw['provider']
  if (typeof raw['model'] === 'string' && raw['model'].trim())
    value.model = raw['model'].trim().slice(0, 200)
  if (raw['operation'] === 'PRACTICE_GENERATION' || raw['operation'] === 'PRACTICE_EXTRACTION')
    value.operation = raw['operation']
  for (const key of ['version', 'page'] as const) {
    const n = Number(raw[key])
    if (
      Number.isInteger(n) &&
      n >= (key === 'page' ? 1 : 0) &&
      n <= (key === 'page' ? 4000 : 2147483647)
    )
      value[key] = n
  }
  if (['RUNNING', 'SUCCEEDED', 'FAILED', 'ABANDONED'].includes(String(raw['status'])))
    value.status = raw['status'] as AIAnalyticsSearch['status'] & string
  if (['EXPERIMENT', 'OVERRIDE', 'DEFAULT', 'EXTRACTION'].includes(String(raw['assignment'])))
    value.assignment = raw['assignment'] as AIAnalyticsSearch['assignment'] & string
  if (['NEWEST', 'OLDEST', 'DURATION_DESC', 'COST_DESC'].includes(String(raw['sort'])))
    value.sort = raw['sort'] as AiDiagnosticsSort
  return value
}
export function analyticsDates(now = new Date()) {
  const to = now.toISOString().slice(0, 10)
  const from = new Date(Date.parse(to) - 29 * 86400000).toISOString().slice(0, 10)
  return { from, to }
}
export function analyticsFilter(
  search: AIAnalyticsSearch,
  now = new Date(),
): AiAnalyticsFilterInput {
  const defaults = analyticsDates(now)
  return {
    from: `${search.from ?? defaults.from}T00:00:00.000Z`,
    to: new Date(Date.parse(search.to ?? defaults.to) + 86400000).toISOString(),
    provider: search.provider ?? null,
    model: search.model ?? null,
    operation: search.operation ?? 'PRACTICE_GENERATION',
    experimentVersion: search.version ?? null,
    status: search.status ?? null,
    selectionMode: search.assignment ?? null,
  }
}
export function aiFormatting(language: Language, t: Translate) {
  const unavailable = t('aiAnalytics.unavailable')
  return {
    number: (v: number | null | undefined) => (v == null ? unavailable : formatNumber(v, language)),
    money: (v: number | null | undefined) =>
      v == null ? unavailable : formatEstimatedUsd(v, language),
    latency: (v: number | null | undefined) =>
      v == null ? unavailable : t('aiAnalytics.latencyUnit', [formatNumber(v, language)]),
    rate: (count: number, samples: number) =>
      samples ? formatPercent((count / samples) * 100, language) : unavailable,
  }
}
export function aiStatus(t: Translate, status: string | null | undefined): string {
  switch (status) {
    case 'RUNNING':
      return t('aiAnalytics.running')
    case 'SUCCEEDED':
      return t('aiAnalytics.succeeded')
    case 'FAILED':
      return t('aiAnalytics.failed')
    case 'ABANDONED':
      return t('aiAnalytics.abandoned')
    case 'TIMEOUT':
      return t('aiAnalytics.errorTimeout')
    case 'RATE_LIMIT':
      return t('aiAnalytics.errorRateLimit')
    case 'REFUSED':
      return t('aiAnalytics.errorRefused')
    case 'INVALID_RESPONSE':
      return t('aiAnalytics.errorInvalidResponse')
    case 'INCOMPLETE_RESPONSE':
      return t('aiAnalytics.errorIncomplete')
    case 'CANCELLED':
      return t('aiAnalytics.errorCancelled')
    case 'NOT_CONFIGURED':
      return t('aiAnalytics.errorNotConfigured')
    case 'PROVIDER_ERROR':
      return t('aiAnalytics.errorProvider')
    case 'INTERNAL_ERROR':
      return t('aiAnalytics.errorInternal')
    case 'DELIVERY_FAILED':
      return t('aiAnalytics.errorDelivery')
    case 'MARKDOWN_PARSING':
      return t('aiAnalytics.errorParsing')
    case 'SCHEMA_VALIDATION':
      return t('aiAnalytics.errorSchema')
    case 'QUESTION_TYPE_MISMATCH':
      return t('aiAnalytics.errorType')
    case 'TERMINAL_STATE_UNAVAILABLE':
      return t('aiAnalytics.errorRecovered')
    case 'LEASE_EXPIRED':
      return t('aiAnalytics.errorLease')
    default:
      return t('aiAnalytics.unavailable')
  }
}
// Provider/model identifiers are vendor names and deliberately remain untranslated.
export function providerName(provider: string | null | undefined) {
  return provider === 'openai' ? 'OpenAI' : provider === 'gemini' ? 'Gemini' : ''
}
